import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { filter } from 'rxjs';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { UserHttpService } from '../../../../../shared/service/user-http.service';
import { OrderStateEnum } from '../../../../../shared/model/order-state.enum';
import {
  AuthorizedUser,
  hasAnyOfThoseRoles,
  User,
  UserRole,
} from '../../../../../shared/model/user.model';
import { Order } from '../../../../order.model';
import { ALL_STATES, ALLOWED_ORDER_STATE_CHANGES } from './status-change-map';

@Component({
  selector: 'hpm-status-change',
  templateUrl: './status-change.component.html',
  styleUrl: './status-change.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StatusChangeComponent),
      multi: true,
    },
  ],
})
export class StatusChangeComponent
  implements ControlValueAccessor, AfterViewInit
{
  allowedStates: OrderStateEnum[] = [];

  selectedStatus!: OrderStateEnum;
  initialStatus: OrderStateEnum = this.selectedStatus;

  currentUser: AuthorizedUser | undefined;
  userAllowedToChangeUser = false;

  @Input() userlist: User[] = [];
  @Input() order!: Order;
  @Output() orderChange: EventEmitter<string | undefined> = new EventEmitter<
    string | undefined
  >();
  @Input() assignedTo: User | undefined;
  @Input() isDisabled = false;
  @Output() statusChange = new EventEmitter<{
    oldStatus: OrderStateEnum;
    newStatus: OrderStateEnum;
    assignedTo: User;
  }>();

  commentControl: FormControl = new FormControl<string>('');

  @ViewChild('statusFormField') statusFormField: MatSelect | undefined;
  @ViewChild('templatePortalContent')
  templatePortalContent: TemplateRef<unknown> | undefined;
  templatePortal: TemplatePortal | undefined;
  overlayRef: OverlayRef | undefined = undefined;

  constructor(
    private cdr: ChangeDetectorRef,
    private viewContainerRef: ViewContainerRef,
    private overlay: Overlay,
    private userService: UserHttpService,
  ) {}

  ngAfterViewInit(): void {
    this.loadCurrentUser();

    if (this.templatePortalContent) {
      this.templatePortal = new TemplatePortal(
        this.templatePortalContent,
        this.viewContainerRef,
      );
    }
  }

  compareUser(user1: User, user2: User): boolean {
    return user2?.id === user1?.id;
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */ // any type required for CVA interface

  onChange: any = () => {};

  onTouched: any = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /* eslint-enable */

  loadCurrentUser(): void {
    this.userService.getCurrentUser().subscribe((user) => {
      if (user) {
        this.currentUser = user;
        this.loadAllowedStatusTransitions();
        this.userAllowedToChangeUser = hasAnyOfThoseRoles(this.currentUser, [
          UserRole.ADMIN,
          UserRole.OFFICE_SERVICE,
        ]);
      } else {
        this.currentUser = undefined;
      }
    });
  }

  loadAllowedStatusTransitions(): void {
    if (this.currentUser && this.currentUser.roles.length > 0) {
      if (
        hasAnyOfThoseRoles(this.currentUser, [
          UserRole.ADMIN,
          UserRole.OFFICE_SERVICE,
        ])
      ) {
        this.allowedStates = ALL_STATES;
      } else {
        this.allowedStates = this.getAllowedStatesForRoles(
          this.currentUser,
          this.order.status,
        );
      }
      this.cdr.detectChanges();
    }
  }

  private getAllowedStatesForRoles(
    currentUser: AuthorizedUser,
    currentState: OrderStateEnum,
  ): OrderStateEnum[] {
    const statesForUser = new Set<OrderStateEnum>();
    currentUser.roles.forEach((role) => {
      const statesForRole =
        ALLOWED_ORDER_STATE_CHANGES.get(role)?.get(currentState);
      if (statesForRole) {
        statesForRole.forEach((state) => statesForUser.add(state));
      }
    });
    return statesForUser ? Array.from(statesForUser) : [];
  }

  writeValue(value: OrderStateEnum): void {
    this.selectedStatus = value;
    this.initialStatus = this.selectedStatus;
    this.loadAllowedStatusTransitions();

    this.cdr.detectChanges();
  }

  closeWithSave(): void {
    const oldStatus = this.initialStatus;
    const newStatus = this.selectedStatus;

    this.initialStatus = this.selectedStatus;
    this.orderChange.emit(this.commentControl.value);
    this.commentControl.reset();

    this.statusChange.emit({
      oldStatus: oldStatus,
      newStatus: newStatus,
      assignedTo: this.assignedTo!,
    });
    this.closeDialog();
  }

  openDialog(event: MatSelectChange): void {
    if (this.overlayRef) {
      return; // do not open overlay if already open
    }
    this.selectedStatus = event.value;
    const overlayConfig = new OverlayConfig();
    overlayConfig.positionStrategy = this.overlay.position().global().end();
    overlayConfig.hasBackdrop = true;
    this.overlayRef = this.overlay.create(overlayConfig);
    this.overlayRef.attach(this.templatePortal);
    this.overlayRef
      .keydownEvents()
      .pipe(filter((event: KeyboardEvent) => event.code === 'Escape'))
      .subscribe(() => this.abortDialog());
    this.overlayRef.backdropClick().subscribe(() => this.abortDialog());
  }

  abortDialog(): void {
    this.selectedStatus = this.initialStatus;
    this.commentControl.reset();
    this.closeDialog();
    this.cdr.detectChanges();
  }

  private closeDialog(): void {
    this.overlayRef?.detach();
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    if (this.statusFormField) {
      // fixme: on esc the medical-practice field gets in focus????
      this.statusFormField?.focus();
    }
  }
}
